import React, {
  useEffect, useMemo, useState, useRef, useCallback, Fragment, RefObject, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import StatusBar from '../../../components/status-bar/status-bar.component';
import ModalLayout from '../../../components/modal-layout/modal-layout.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import Loader from '../../../components/loader/loader.component';
import Image from '../../../components/image/image.component';
import Button from '../../../components/button/button.component';
import ModalButtonBlock from '../../../components/modal-button-block/modal-button-block.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import Hint from '../../../components/hint/hint.component';
import Grid from '../../../components/grid/grid.component';
import GridItem from '../../../components/grid-item/grid-item.component';
import ActionButton from '../../../components/action-button/action-button.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import Form from '../../../components/form/form.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect from '../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FormFieldPhone from '../../../components/form-field-phone/form-field-phone.component';
import FormFieldUpload from '../../../components/form-field-upload/form-field-upload.component';
import FieldSeparatedBlock from '../../../components/field-separated-block/field-separated-block.component';
import ImageFileContainer from '../../../components/image-file-container/image-file-container.component';
import MenuItemAddNewItem from '../../../components/menu-item-add-new-item/menu-item-add-new-item.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import CommentTable from '../../app/common/comment-table/comment-table.component';
import CommentForm from '../../app/common/comment-form/comment-form.component';
import RemoveIcon from '../../../icons/remove-icon';
import CancelFilledIcon from '../../../icons/cancel-filled-icon';
import StructureService from '../../../services/structure-service';
import CommentService from '../../../services/comment-service';
import FormatService from '../../../services/format-service';
import FormService from '../../../services/form-service';
import useScreenSmall from '../../../hooks/use-screen-small';
import { FILE_EXTENSION_LIST_IMAGE } from '../../../constants/system/file-extensions';
import {
  ACTIVITY_COMMENT_CREATE, ACTIVITY_COMMENT_DELETE, ACTIVITY_COMMENT_EDIT, ACTIVITY_COMMENT_LIST_VIEW,
  CONTACT_CREATE, CONTACT_VIEW, DEAL_CREATE, DEAL_LIST_VIEW, DEAL_VIEW, SEARCH_RESULT_VIEW,
} from '../../../constants/entity/permissions';
import {
  KEYBOARD_KEY_ARROW_UP, KEYBOARD_KEY_ENTER, KEYBOARD_KEY_ESCAPE,
} from '../../../constants/system/keyboard-keys';
import { DATA_CONTACT_ID } from '../../../constants/system/data-attributes';
import { SIGN_HASH, SIGN_AT } from '../../../constants/system/signs';
import { ACTIVITY_CATEGORY_NEW_DEAL } from '../../../constants/entity/activity-categories';
import { ROUTE_CONTACT, ROUTE_SEARCH } from '../../../constants/system/routes';
import { HTML_TAG_NAME_ANCHOR } from '../../../constants/system/html-tag-names';
import PersonDefaultImage from '../../../static/images/person-default-image.png';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';
import AssetDefaultImage from '../../../static/images/asset-default-image.png';

const ActivityModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLUMNS_TOTAL: number = 2;
  const COMMENT_ENTITY_TYPE_CONTACT: string = 'contact';
  const MODAL_TITLE_NEW: string = 'New Activity';
  const MODAL_TITLE_DEFAULT: string = 'Activity';

  const {
    isOpen,
    isSaving,
    isSaveDisabled,
    isCommentSaving,
    isEditMode,
    isDealActivity,
    isNewActivity,
    isNewDealFormVisible,
    isNewDealSourceFormVisible,
    isNewDealContactFormVisible,
    isCommentFormActive,
    withFixedFooter,
    dealId,
    activityData,
    queryCommentId,
    currentUser,
    dealPhotoFile,
    activityFieldNewItems,
    tagList,
    dealList,
    assetList,
    contactList,
    contactCompanyList,
    contactPersonList,
    contactPersonInternalList,
    contactGroupList,
    contactLinkedUserList,
    measureUnitList,
    activityStatusList,
    activityCategoryList,
    activityTypeList,
    activityInterestLevelList,
    saveFormDeps,
    onCreate,
    onCancel,
    onCloseModal,
    onAddComment,
    onUpdateComment,
    onDeleteComment,
    onOpenNewDealForm,
    onCloseNewDealForm,
    onOpenNewDealSourceForm,
    onCloseNewDealSourceForm,
    onOpenNewDealContactForm,
    onCloseNewDealContactForm,
    onFieldDeleteClick,
    onAddNewFieldItem,
    onDealPhotoChange,
    onDealPhotoDelete,
    onTagsChange,
    onDealContactsChange,
    onDealContactGroupsChange,
    onDealContactPhoneChange,
    onSubmitCreateForm,
    onActivityDealSearch,
    onActivityDealSearchClose,
    onDealAssetSearch,
    onDealAssetSearchClose,
    onContactSearch,
    onContactSearchClose,
    onContactCompanySearch,
    onContactCompanySearchClose,
    onContactPersonSearch,
    onContactPersonSearchClose,
    onTagSearch,
    onTagSearchClose,
    onCommentEntityLinkClick,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();
  const contentContainerRef: RefObject<HTMLDivElement> | null = useRef(null);

  const [commentText, setCommentText]: [string | null, Function] = useState(null);
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [isMessageError, setIsMessageError]: [boolean, Function] = useState(false);
  const [commentAttachments, setCommentAttachments]: [AttachmentInput[] | null, Function]
    = useState<StateValueDefault>(null);
  const [commentAttachmentFiles, setCommentAttachmentFiles]: [File[] | null, Function]
    = useState<StateValueDefault>(null);
  const [commentEditId, setCommentEditId]: [number | null, Function] = useState(null);
  const [commentActiveId, setCommentActiveId]: [number | null, Function] = useState(null);
  const [activityFieldItems, setActivityFieldItems]: [Record<string, ActivityFieldInput> | null, Function]
    = useState(null);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [selectedTags, setSelectedTags]: [Tag[] | null, Function] = useState(null);
  const [selectedDealContacts, setSelectedDealContacts]: [Contact[] | null, Function] = useState(null);
  const [selectedDealContactGroups, setSelectedDealContactGroups]: [ContactGroup[] | null, Function] = useState(null);
  const [selectedContactCompanyContacts, setSelectedContactCompanyContacts]: [Contact[] | null, Function]
    = useState(null);
  const [selectedContactPersonContacts, setSelectedContactPersonContacts]: [Contact[] | null, Function]
    = useState(null);

  const commentList: Comment[] = (activityData && activityData.comments) || [];
  const isCommentEditing: boolean = !!commentEditId;

  const commentAttachmentDropDeps: DepsItem[] = useMemo((): DepsItem[] => [
    commentAttachmentFiles,
  ], [commentAttachmentFiles]);
  const dealListViewPermissions: string[] = useMemo((): string[] => [DEAL_LIST_VIEW], []);
  const activityCommentListViewPermissions: string[] = useMemo((): string[] => [ACTIVITY_COMMENT_LIST_VIEW], []);
  const activityCommentCreatePermissions: string[] = useMemo((): string[] => [ACTIVITY_COMMENT_CREATE], []);
  const activityCommentEditPermissions: string[] = useMemo((): string[] => [ACTIVITY_COMMENT_EDIT], []);
  const activityCommentDeletePermissions: string[] = useMemo((): string[] => [ACTIVITY_COMMENT_DELETE], []);
  const commonAttachmentFiles: (AttachmentInput | File)[] = useMemo((): (AttachmentInput | File)[] => [
    ...(commentAttachments || []),
    ...(commentAttachmentFiles || []),
  ], [commentAttachments, commentAttachmentFiles]);

  const mentionList: MentionItem[] = useMemo((): MentionItem[] => (
    contactLinkedUserList && contactLinkedUserList.length
      ? contactLinkedUserList.map((contactItem: Contact): MentionItem => ({
        id: `${SIGN_AT}${contactItem.fullName}`,
        contactId: contactItem.id,
        fullName: contactItem.fullName,
        photo: contactItem.photo,
        photoThumbnail: contactItem.photoThumbnail,
      })) : []
  ), [contactLinkedUserList]);

  const hashtagList: HashtagItem[] = useMemo((): HashtagItem[] => (
    tagList && tagList.length ? tagList.map((tagItem: Tag): HashtagItem => ({
      id: `${SIGN_HASH}${tagItem.name}`,
      tagId: tagItem.id,
      name: tagItem.name,
    })) : []
  ), [tagList]);

  const modalTitle: string = useMemo((): string => {
    if (isNewActivity) {
      return MODAL_TITLE_NEW;
    }

    if (activityData && activityData.deal && activityData.deal.name) {
      return activityData.deal.name;
    }

    return MODAL_TITLE_DEFAULT;
  }, [isNewActivity, activityData]);

  const currentActivityId: number | null = (activityData && activityData.id) || null;
  const activityId: number = !isNewActivity && currentActivityId ? currentActivityId : FormService.FORM_KEY_NEW;
  const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityId);
  const formPrefix: string = isDealActivity ? `dealData.activityListData[${formKeyActivity}]`
    : `activityListData[${formKeyActivity}]`;
  const activityCategory: string = activityData && activityData.category;
  const isNewDealCategory: boolean = activityCategory === ACTIVITY_CATEGORY_NEW_DEAL;

  const menuItemActivityStatusList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: activityStatusList,
  }), [activityStatusList]);

  const menuItemActivityCategoryList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: activityCategoryList,
  }), [activityCategoryList]);

  const menuItemActivityTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: activityTypeList,
  }), [activityTypeList]);

  const menuItemDealList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'name',
    optionList: dealList,
    optionItemImageKey: 'mainPhoto',
    optionItemImageThumbnailKey: 'mainPhotoThumbnail',
    defaultImage: AssetDefaultImage,
  }), [dealList]);

  const menuItemAssetList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'name',
    optionList: assetList,
    optionItemImageKey: 'mainPhoto',
    optionItemImageThumbnailKey: 'mainPhotoThumbnail',
    defaultImage: AssetDefaultImage,
  }), [assetList]);

  const menuItemContactCompanyList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
  }), [contactCompanyList]);

  const menuItemContactDealContactList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: isNewDealCategory ? contactPersonInternalList : contactPersonList,
    defaultImage: PersonDefaultImage,
    selectedItemList: selectedDealContacts,
  }), [contactPersonInternalList, contactPersonList, isNewDealCategory, selectedDealContacts]);

  const menuItemContactGroupList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: contactGroupList,
    selectedItemList: selectedDealContactGroups,
  }), [contactGroupList, selectedDealContactGroups]);

  const menuItemMeasureUnitList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: measureUnitList,
  }), [measureUnitList]);

  const menuItemActivityInterestLevelList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: activityInterestLevelList,
  }), [activityInterestLevelList]);

  const menuItemContactList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactList,
    defaultImage: CompanyDefaultImage,
  }), [contactList]);

  const menuItemTagList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionItemLabelKey: 'name',
    optionList: tagList,
    selectedItemList: selectedTags,
  }), [tagList, selectedTags]);

  const resetForm: Function = useCallback((): void => {
    setSelectedTags(null);
    setSelectedDealContacts(null);
    setSelectedDealContactGroups(null);
  }, []);

  const dealContactGroupsChange: Function = useCallback((contactGroupValues: ContactGroup[]): void => {
    if (onDealContactGroupsChange) {
      onDealContactGroupsChange(activityId, contactGroupValues);
    }

    setSelectedDealContactGroups(contactGroupValues);
  }, [activityId]);

  const dealContactPhoneChange: PhoneChangeHandlerFunction = useCallback((phoneValue?: string): void => {
    if (onDealContactPhoneChange) {
      onDealContactPhoneChange(activityId, phoneValue);
    }
  }, [activityId]);

  const cancelChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    if (onCancel && !isEditMode) {
      onCancel();
    } else if (onCloseModal) {
      onCloseModal();
    }

    if (onCloseNewDealForm) {
      onCloseNewDealForm(activityId);
    }

    if (onCloseNewDealSourceForm) {
      onCloseNewDealSourceForm(activityId);
    }

    resetForm();

    setCommentAttachmentFiles(null);
    setCommentAttachments(null);
    setCommentActiveId(null);
    setCommentEditId(null);
    setCommentText(null);
    setSelectedContactCompanyContacts(null);
    setSelectedContactPersonContacts(null);
  }, [activityId, isEditMode]);

  const onSaveCommentSuccess: Function = useCallback((commentId?: number): void => {
    const lastComment: Comment | null = commentList && commentList.length ? commentList[commentList.length - 1] : null;
    const resultCommentId: number | null = commentId || (lastComment && lastComment.id) || null;

    if (resultCommentId) {
      CommentService.scrollToComment(resultCommentId);
    }
  }, [commentList]);

  const saveComment: Function = useCallback((): void => {
    const mentionContactIds: number[] = commentText
      ? FormatService.extractHtmlAttributes(commentText, HTML_TAG_NAME_ANCHOR, DATA_CONTACT_ID, true) as number[] : [];

    const linkedEntities: CommentEntityInput[] = [];
    if (selectedContactCompanyContacts && (selectedContactCompanyContacts as Contact[]).length) {
      (selectedContactCompanyContacts as Contact[]).forEach((contactItem: Contact): void => {
        linkedEntities.push({
          entityId: contactItem.id,
          entityType: COMMENT_ENTITY_TYPE_CONTACT,
        });
      });
    }

    if (selectedContactPersonContacts && (selectedContactPersonContacts as Contact[]).length) {
      (selectedContactPersonContacts as Contact[]).forEach((contactItem: Contact): void => {
        linkedEntities.push({
          entityId: contactItem.id,
          entityType: COMMENT_ENTITY_TYPE_CONTACT,
        });
      });
    }

    if (commentEditId) {
      if (onUpdateComment && (commentText || (commentAttachments && commentAttachments.length)
        || (commentAttachmentFiles && commentAttachmentFiles.length))) {
        onUpdateComment(
          currentActivityId, commentText, commentAttachments, commentAttachmentFiles, commentEditId, mentionContactIds,
          onSaveCommentSuccess,
        );
      }

      setCommentEditId(null);
    } else if (onAddComment && (commentText || (commentAttachmentFiles && commentAttachmentFiles.length))) {
      onAddComment(
        currentActivityId, commentText, commentAttachmentFiles, mentionContactIds, linkedEntities, onSaveCommentSuccess,
      );
    }

    setCommentText(null);
    setCommentAttachmentFiles(null);
    setCommentAttachments(null);
    setSelectedContactPersonContacts(null);
    setSelectedContactCompanyContacts(null);
  }, [
    commentText, commentAttachmentFiles, commentAttachments, commentEditId, currentActivityId,
    selectedContactCompanyContacts, selectedContactPersonContacts, commentList,
  ]);

  const onCompanyContactsChange: Function = useCallback((contactValues: Contact): void => {
    setSelectedContactCompanyContacts(contactValues);
  }, []);

  const onPersonContactsChange: Function = useCallback((contactValues: Contact): void => {
    setSelectedContactPersonContacts(contactValues);
  }, []);

  const processApplyChangesAndCloseModal: Function = useCallback((): void => {
    if (isNewActivity && onCreate) {
      onCreate();
      setSelectedDealContacts(null);
    } else if (onCloseModal) {
      onCloseModal();
    }

    if (commentText || (commentAttachmentFiles && commentAttachmentFiles.length)) {
      saveComment();
    }

    if (commentText || (commentAttachmentFiles && commentAttachmentFiles.length)) {
      setStatusMessage('Saved successfully');
      setIsOpenMessage(true);
      setIsMessageError(false);
    }

    setCommentAttachmentFiles(null);
    setCommentAttachments(null);
    setSelectedContactCompanyContacts(null);
    setSelectedContactPersonContacts(null);
  }, [isNewActivity, commentText, commentAttachmentFiles, isSaveDisabled, isEditMode, ...(saveFormDeps || [])]);

  const applyChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
      if (onSubmitCreateForm) {
        onSubmitCreateForm((): void => {
          processApplyChangesAndCloseModal();
        });
      } else {
        processApplyChangesAndCloseModal();
      }
    }, [
      isNewActivity, commentText, commentAttachmentFiles, activityData, isSaveDisabled, isEditMode,
      ...(saveFormDeps || []),
    ],
  );

  const dealPhotoChange: Function = useCallback((file: File): void => {
    if (onDealPhotoChange) {
      onDealPhotoChange(activityId, file);
    }
  }, [activityId]);

  const dealPhotoDelete: Function = useCallback((event: React.ChangeEvent): void => {
    if (onDealPhotoDelete) {
      onDealPhotoDelete(activityId);
    }

    event.stopPropagation();
  }, [activityId]);

  const onCommentFieldChange: Function = useCallback((event: React.ChangeEvent, commentTextValue: string): void => {
    setCommentText(commentTextValue);
  }, []);

  const onCommentFieldKeyDown: Function = useCallback((event: React.KeyboardEvent, commentTextValue: string): void => {
    if (event.key === KEYBOARD_KEY_ARROW_UP && !commentTextValue) {
      CommentService.editLastComment();

      event.preventDefault();
    }

    if (event.key === KEYBOARD_KEY_ESCAPE) {
      onCancelCommentForm();
    }

    if (event.key === KEYBOARD_KEY_ENTER && !event.shiftKey) {
      setCommentText(commentTextValue);

      CommentService.sendComment();
    }
  }, []);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setIsMessageError(false);
    setStatusMessage('');
  }, []);

  const openNewDealForm: MouseEventHandlerFunction = useCallback((): void => {
    if (onOpenNewDealForm) {
      onOpenNewDealForm(activityId);
    }
  }, [activityId]);

  const closeNewDealForm: MouseEventHandlerFunction = useCallback((): void => {
    if (onCloseNewDealForm) {
      onCloseNewDealForm(activityId);
    }
  }, [activityId]);

  const openNewDealSourceForm: MouseEventHandlerFunction = useCallback((): void => {
    if (onOpenNewDealSourceForm) {
      onOpenNewDealSourceForm(activityId);
    }
  }, [activityId]);

  const closeNewDealSourceForm: MouseEventHandlerFunction = useCallback((): void => {
    if (onCloseNewDealSourceForm) {
      onCloseNewDealSourceForm(activityId);
    }
  }, [activityId]);

  const openNewDealContactForm: MouseEventHandlerFunction = useCallback((): void => {
    if (onOpenNewDealContactForm) {
      onOpenNewDealContactForm(activityId);
    }
  }, [activityId]);

  const closeNewDealContactForm: MouseEventHandlerFunction = useCallback((): void => {
    if (onCloseNewDealContactForm) {
      onCloseNewDealContactForm(activityId);
    }
  }, [activityId]);

  const switchCommentEditMode: Function = useCallback((event: React.SyntheticEvent, comment: Comment): void => {
    const isEditModeActive: boolean = !commentEditId || commentEditId !== comment.id;

    setCommentEditId(isEditModeActive ? comment.id : null);
    setCommentText(isEditModeActive ? (comment.text || '') : null);

    const commentAttachmentItems: Attachment[] | null = comment.attachments && comment.attachments.length
      ? comment.attachments : null;
    setCommentAttachments(isEditModeActive && commentAttachmentItems
      ? commentAttachmentItems.map((attachmentItem: Attachment): AttachmentInput => ({
        id: attachmentItem.id,
        name: attachmentItem.file.name,
        link: attachmentItem.file.linkName,
      })) : null);
    setCommentAttachmentFiles(isEditModeActive ? [] : null);

    event.stopPropagation();
  }, [commentEditId, commentList]);

  const onCommentAttachmentFileDelete: Function = useCallback((
    event: React.ChangeEvent, fileIndex: number,
  ): void => {
    const isCommentEditing: boolean = !!commentEditId;
    const currentCommentAttachmentFiles: File[] = [...(commentAttachmentFiles || [])];
    const currentCommentAttachments: AttachmentInput[] = [...(commentAttachments || [])];

    if (fileIndex !== null && fileIndex >= 0) {
      if (isCommentEditing && fileIndex < currentCommentAttachments.length) {
        const newCommentAttachments: AttachmentInput[] = currentCommentAttachments;
        StructureService.deleteListItemByIndex(newCommentAttachments, fileIndex);
        setCommentAttachments(newCommentAttachments);
      } else {
        const newCommentAttachmentFiles: File[] = currentCommentAttachmentFiles;
        StructureService.deleteListItemByIndex(
          newCommentAttachmentFiles,
          fileIndex - (isCommentEditing ? currentCommentAttachments.length : 0),
        );
        setCommentAttachmentFiles(newCommentAttachmentFiles);
      }
    }
  }, [commentAttachmentFiles, commentAttachments]);

  const onCommentAttachmentFilesChange: Function = useCallback((files: File[]): void => {
    setCommentAttachmentFiles([...(commentAttachmentFiles || []), ...files]);
  }, [commentAttachmentFiles]);

  const onCommentAttachmentFileError: Function = useCallback((error: string): void => {
    setStatusMessage(error);
    setIsOpenMessage(true);
    setIsMessageError(true);
  }, []);

  const addNewItem: MouseEventHandlerFunction = useCallback((): void => {
    if (onAddNewFieldItem) {
      onAddNewFieldItem({
        id: StructureService.getNewItemId(activityFieldNewItems || [], []),
        name: '',
        value: '',
      });
    }
  }, [activityData, activityFieldNewItems]);

  const deleteClick: Function = useCallback((activityFieldId: number): void => {
    if (onFieldDeleteClick) {
      onFieldDeleteClick(activityId, activityFieldId);
    }

    if (activityFieldItems) {
      const newActivityFieldItems: Record<string, ActivityFieldInput> = activityFieldItems;
      delete newActivityFieldItems[activityFieldId];
      setActivityFieldItems({ ...newActivityFieldItems });
    }
  }, [activityId, activityFieldItems]);

  const tagsChange: Function = useCallback((tagValues: Tag[]): void => {
    if (onTagsChange) {
      onTagsChange(activityId, tagValues);
    }

    setSelectedTags(tagValues);
  }, [activityId]);

  const dealContactsChange: Function = useCallback((contactValues: Contact): void => {
    if (onDealContactsChange) {
      onDealContactsChange(activityId, contactValues);
    }

    setSelectedDealContacts(contactValues);
  }, [activityId]);

  const onDeleteCommentCancel: Function = useCallback((event: React.ChangeEvent, commentId: number): void => {
    if (commentActiveId === commentId) {
      setCommentActiveId(null);
    }

    event.stopPropagation();
  }, [commentActiveId]);

  const onCancelCommentForm: Function = useCallback((): void => {
    setCommentAttachmentFiles(null);
    setCommentAttachments(null);
    setCommentActiveId(null);
    setCommentEditId(null);
    setCommentText(null);
    setSelectedContactCompanyContacts(null);
    setSelectedContactPersonContacts(null);
  }, []);

  const onDeleteCommentConfirm: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, commentId: number,
  ): void => {
    if (onDeleteComment) {
      onDeleteComment(event, currentActivityId, commentId);
    }

    onCancelCommentForm();

    event.stopPropagation();
  }, [currentActivityId, commentList]);

  const onCommentActiveIdChange: Function = useCallback((
    event: React.ChangeEvent,
    commentId: number,
  ): void => {
    setCommentActiveId(commentId);

    event.stopPropagation();
  }, []);

  const closeModalByClickAway: Function = useCallback((): void => {
    if (!isSaveDisabled && isNewActivity && !isEditMode) {
      setIsLeaveConfirmModalOpen(true);
    } else if (onCloseModal) {
      onCloseModal();
    }
  }, [isSaveDisabled, isNewActivity, isEditMode]);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);

    resetForm();

    if (onCancel) {
      onCancel();
    }

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  useEffect((): void => {
    if (activityData) {
      const initialActivityFieldItems: Record<string, ActivityFieldInput> = {};
      if (activityData.options && activityData.options.length) {
        activityData.options.forEach((activityFieldItem: ActivityField): void => {
          initialActivityFieldItems[activityFieldItem.id] = {
            ...activityFieldItem,
          };
        });
      }
      setActivityFieldItems(initialActivityFieldItems);

      if (!selectedTags) {
        setSelectedTags(activityData.tags || []);
      }

      if (!selectedDealContacts) {
        setSelectedDealContacts(activityData.dealContacts && activityData.dealContacts.length
          ? activityData.dealContacts : []);
      }
    }
  }, [activityData]);

  return (
    <Fragment>
      {
        (!!activityData || isNewActivity) && (
          <ModalLayout
            isOpen={isOpen}
            title={modalTitle}
            onCloseModal={cancelChangesAndCloseModal}
            onCloseModalByClickAway={closeModalByClickAway}
            imageComponent={(
              !isNewActivity && activityData.deal && (
                <FilteredLink
                  isExternal
                  link={activityData.deal.mainPhoto || activityData.deal.mainPhotoThumbnail}
                  permission={DEAL_VIEW}
                >
                  <Image
                    isSquare
                    text={activityData.deal.name}
                    link={activityData.deal.mainPhotoThumbnail || activityData.deal.mainPhoto || AssetDefaultImage}
                  />
                </FilteredLink>
              )
            )}
          >
            <Grid withSmallSpacing>
              {
                isNewActivity && (
                  <GridItem isFullWidth>
                    <Card isContainer isFullHeight withNoPadding ref={contentContainerRef}>
                      <Form>
                        <Table>
                          <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
                            <TableRow>
                              <TableCell>
                                Status
                                <RequiredMark />
                              </TableCell>
                              <TableCell>
                                <FormFieldSelect
                                  isBordered
                                  isDisabled={activityStatusList && !activityStatusList.length}
                                  name={`${formPrefix}.status`}
                                  optionsTotal={activityStatusList && activityStatusList.length}
                                >
                                  {menuItemActivityStatusList}
                                </FormFieldSelect>
                              </TableCell>
                            </TableRow>
                            {
                              isDealActivity && (
                                <TableRow>
                                  <TableCell>
                                    Category
                                    <RequiredMark />
                                  </TableCell>
                                  <TableCell>
                                    <FormFieldSelect
                                      isDisabled
                                      isBordered
                                      name={`${formPrefix}.category`}
                                      optionsTotal={activityCategoryList && activityCategoryList.length}
                                    >
                                      {menuItemActivityCategoryList}
                                    </FormFieldSelect>
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            <TableRow>
                              <TableCell>
                                Type
                                <RequiredMark />
                              </TableCell>
                              <TableCell>
                                <FormFieldSelect
                                  isBordered
                                  isDisabled={activityTypeList && !activityTypeList.length}
                                  name={`${formPrefix}.type`}
                                  optionsTotal={activityTypeList && activityTypeList.length}
                                >
                                  {menuItemActivityTypeList}
                                </FormFieldSelect>
                              </TableCell>
                            </TableRow>
                            {
                              !isNewDealFormVisible && !dealId && (
                                <TableRow>
                                  <TableCell>
                                    Deal
                                    <RequiredMark />
                                  </TableCell>
                                  <TableCell>
                                    <FormFieldSelect
                                      isBordered
                                      withSearch
                                      withBlankElement
                                      name={`${formPrefix}.dealId`}
                                      optionsTotal={dealList && dealList.length}
                                      onSearch={onActivityDealSearch}
                                      onClose={onActivityDealSearchClose}
                                      newItemComponent={(
                                        !isNewDealFormVisible && (
                                          <MenuItemAddNewItem
                                            label="New Deal"
                                            createPermission={DEAL_CREATE}
                                            onClick={openNewDealForm}
                                          />
                                        )
                                      )}
                                    >
                                      {menuItemDealList}
                                    </FormFieldSelect>
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            {
                              isNewDealFormVisible && (
                                <TableRow>
                                  <TableCell>
                                    Deal (new)
                                  </TableCell>
                                  <TableCell isTextRight>
                                    <ActionButton onClick={closeNewDealForm}>
                                      <CancelFilledIcon />
                                    </ActionButton>
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            {
                              isNewDealFormVisible && (
                                <TableRow>
                                  <TableCell>
                                    Deal Name
                                    <RequiredMark />
                                  </TableCell>
                                  <TableCell>
                                    <FormFieldText
                                      isBordered
                                      withAutoFocus={isNewDealFormVisible}
                                      name={`${formPrefix}.deal.name`}
                                    />
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            {
                              isNewDealFormVisible && (
                                <TableRow>
                                  <TableCell>
                                    Deal Photo
                                  </TableCell>
                                  <TableCell>
                                    <ImageFileContainer withNoOffset>
                                      <FormFieldUpload
                                        isImage
                                        isDroppable
                                        isSmallImage
                                        name={`${formPrefix}.deal.mainPhoto`}
                                        uploadFile={dealPhotoFile}
                                        buttonLabel="New Deal"
                                        acceptedExtensionList={FILE_EXTENSION_LIST_IMAGE}
                                        defaultPreviewUrl={AssetDefaultImage}
                                        onFileChange={dealPhotoChange}
                                        onFileDelete={dealPhotoDelete}
                                      />
                                    </ImageFileContainer>
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            {
                              isNewDealFormVisible && (
                                <TableRow>
                                  <TableCell>
                                    Deal Asset
                                    <RequiredMark />
                                  </TableCell>
                                  <TableCell>
                                    <FormFieldSelect
                                      isBordered
                                      withSearch
                                      withBlankElement
                                      isDisabled={assetList && !assetList.length}
                                      name={`${formPrefix}.deal.assetId`}
                                      optionsTotal={assetList && assetList.length}
                                      onSearch={onDealAssetSearch}
                                      onClose={onDealAssetSearchClose}
                                    >
                                      {menuItemAssetList}
                                    </FormFieldSelect>
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            {
                              !isNewDealSourceFormVisible && (
                                <TableRow>
                                  <TableCell>
                                    Deal Source
                                  </TableCell>
                                  <TableCell>
                                    <FormFieldSelect
                                      isBordered
                                      withSearch
                                      withBlankElement
                                      name={`${formPrefix}.dealSourceId`}
                                      optionsTotal={contactCompanyList && contactCompanyList.length}
                                      onSearch={onContactCompanySearch}
                                      onClose={onContactCompanySearchClose}
                                      newItemComponent={(
                                        !isNewDealSourceFormVisible && (
                                          <MenuItemAddNewItem
                                            label="New Deal Source"
                                            createPermission={CONTACT_CREATE}
                                            onClick={openNewDealSourceForm}
                                          />
                                        )
                                      )}
                                    >
                                      {menuItemContactCompanyList}
                                    </FormFieldSelect>
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            {
                              isNewDealSourceFormVisible && (
                                <TableRow>
                                  <TableCell>
                                    Deal Source (new)
                                  </TableCell>
                                  <TableCell isTextRight>
                                    <ActionButton onClick={closeNewDealSourceForm}>
                                      <CancelFilledIcon />
                                    </ActionButton>
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            {
                              isNewDealSourceFormVisible && (
                                <TableRow>
                                  <TableCell>
                                    Deal Source Name
                                    <RequiredMark />
                                  </TableCell>
                                  <TableCell>
                                    <FormFieldText
                                      isBordered
                                      withAutoFocus={isNewDealSourceFormVisible}
                                      name={`${formPrefix}.dealSource.name`}
                                    />
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            {
                              isNewDealSourceFormVisible && (
                                <TableRow>
                                  <TableCell>
                                    Deal Source Website
                                    <Hint
                                      title={'Fill in Website field to search and automatically populate other fields '
                                        + 'of the company, if it is listed in our dictionary'}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <FormFieldText
                                      isBordered
                                      name={`${formPrefix}.dealSource.website`}
                                    />
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            <TableRow>
                              <TableCell>
                                Sponsor
                              </TableCell>
                              <TableCell>
                                <FormFieldSelect
                                  isBordered
                                  withSearch
                                  withBlankElement
                                  isDisabled={contactCompanyList && !contactCompanyList.length}
                                  name={`${formPrefix}.sponsorId`}
                                  optionsTotal={contactCompanyList && contactCompanyList.length}
                                  onSearch={onContactCompanySearch}
                                  onClose={onContactCompanySearchClose}
                                >
                                  {menuItemContactCompanyList}
                                </FormFieldSelect>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Broker
                              </TableCell>
                              <TableCell>
                                <FormFieldSelect
                                  isBordered
                                  withSearch
                                  withBlankElement
                                  isDisabled={contactCompanyList && !contactCompanyList.length}
                                  name={`${formPrefix}.brokerId`}
                                  optionsTotal={contactCompanyList && contactCompanyList.length}
                                  onSearch={onContactCompanySearch}
                                  onClose={onContactCompanySearchClose}
                                >
                                  {menuItemContactCompanyList}
                                </FormFieldSelect>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Lender
                              </TableCell>
                              <TableCell>
                                <FormFieldSelect
                                  isBordered
                                  withSearch
                                  withBlankElement
                                  isDisabled={contactCompanyList && !contactCompanyList.length}
                                  name={`${formPrefix}.lenderId`}
                                  optionsTotal={contactCompanyList && contactCompanyList.length}
                                  onSearch={onContactCompanySearch}
                                  onClose={onContactCompanySearchClose}
                                >
                                  {menuItemContactCompanyList}
                                </FormFieldSelect>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Deal Contacts
                              </TableCell>
                              <TableCell>
                                <FormFieldMultiSelect
                                  isBordered
                                  withImage
                                  withSearch
                                  isDisabled={isNewDealCategory ? true
                                    : (contactPersonList && !contactPersonList.length)}
                                  nameKey="fullName"
                                  name={`${formPrefix}.dealContactIds`}
                                  defaultImage={PersonDefaultImage}
                                  optionList={isNewDealCategory ? contactPersonInternalList : contactPersonList}
                                  onValuesChange={dealContactsChange}
                                  onSearch={isNewDealCategory ? undefined : onContactPersonSearch}
                                  onClose={isNewDealCategory ? undefined : onContactPersonSearchClose}
                                  newItemComponent={(
                                    !isNewDealContactFormVisible && (
                                      <MenuItemAddNewItem
                                        label="New Deal Contact"
                                        createPermission={CONTACT_CREATE}
                                        onClick={openNewDealContactForm}
                                      />
                                    )
                                  )}
                                >
                                  {menuItemContactDealContactList}
                                </FormFieldMultiSelect>
                              </TableCell>
                            </TableRow>
                            {
                              isNewDealContactFormVisible && (
                                <TableRow>
                                  <TableCell>
                                    Deal Contact (new)
                                  </TableCell>
                                  <TableCell isTextRight>
                                    <ActionButton onClick={closeNewDealContactForm}>
                                      <CancelFilledIcon />
                                    </ActionButton>
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            {
                              isNewDealContactFormVisible && (
                                <TableRow>
                                  <TableCell>
                                    Deal Contact First Name
                                    <RequiredMark />
                                  </TableCell>
                                  <TableCell>
                                    <FormFieldText isBordered name={`${formPrefix}.dealContact.firstName`} />
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            {
                              isNewDealContactFormVisible && (
                                <TableRow>
                                  <TableCell>
                                    Deal Contact Last Name
                                    <RequiredMark />
                                  </TableCell>
                                  <TableCell>
                                    <FormFieldText isBordered name={`${formPrefix}.dealContact.lastName`} />
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            {
                              isNewDealContactFormVisible && (
                                <TableRow>
                                  <TableCell>
                                    Deal Contact Email
                                    <RequiredMark />
                                  </TableCell>
                                  <TableCell>
                                    <FormFieldText isBordered name={`${formPrefix}.dealContact.email`} />
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            {
                              isNewDealContactFormVisible && (
                                <TableRow>
                                  <TableCell>
                                    Deal Contact Phone
                                  </TableCell>
                                  <TableCell>
                                    <FormFieldPhone
                                      isBordered
                                      name={`${formPrefix}.dealContact.phone`}
                                      onChange={dealContactPhoneChange}
                                    />
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            {
                              isNewDealContactFormVisible && (
                                <TableRow>
                                  <TableCell>
                                    Deal Contact Group
                                    <RequiredMark />
                                  </TableCell>
                                  <TableCell>
                                    <FormFieldMultiSelect
                                      isBordered
                                      isDisabled={contactGroupList && !contactGroupList.length}
                                      keyField="name"
                                      name={`${formPrefix}.dealContact.groupIds`}
                                      optionList={contactGroupList}
                                      onValuesChange={dealContactGroupsChange}
                                    >
                                      {menuItemContactGroupList}
                                    </FormFieldMultiSelect>
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            {
                              isNewDealContactFormVisible && (
                                <TableRow>
                                  <TableCell>
                                    Deal Contact Company
                                  </TableCell>
                                  <TableCell>
                                    <FormFieldSelect
                                      isBordered
                                      withSearch
                                      withBlankElement
                                      isDisabled={contactCompanyList && !contactCompanyList.length}
                                      name={`${formPrefix}.dealContact.companyId`}
                                      optionsTotal={contactCompanyList && contactCompanyList.length}
                                      onSearch={onContactCompanySearch}
                                      onClose={onContactCompanySearchClose}
                                    >
                                      {menuItemContactCompanyList}
                                    </FormFieldSelect>
                                  </TableCell>
                                </TableRow>
                              )
                            }
                            <TableRow>
                              <TableCell>
                                Total Capitalization
                              </TableCell>
                              <TableCell>
                                <FormFieldNumber isBordered name={`${formPrefix}.amount`} />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Price Guidance
                              </TableCell>
                              <TableCell>
                                <FormFieldText isBordered name={`${formPrefix}.pricing`} />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Size
                              </TableCell>
                              <TableCell>
                                <FieldSeparatedBlock withOffset>
                                  <FormFieldNumber isBordered name={`${formPrefix}.size`} />
                                  <FormFieldSelect
                                    isBordered
                                    withBlankElement
                                    isDisabled={measureUnitList && !measureUnitList.length}
                                    name={`${formPrefix}.sizeType`}
                                    optionsTotal={measureUnitList && measureUnitList.length}
                                  >
                                    {menuItemMeasureUnitList}
                                  </FormFieldSelect>
                                </FieldSeparatedBlock>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                LTV
                              </TableCell>
                              <TableCell>
                                <FormFieldNumber
                                  isPercent
                                  isFloat
                                  isBordered
                                  withNoLimit
                                  name={`${formPrefix}.ltv`}
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Purchase Price
                              </TableCell>
                              <TableCell>
                                <FormFieldNumber isBordered name={`${formPrefix}.purchasePrice`} />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                CapEx
                              </TableCell>
                              <TableCell>
                                <FormFieldNumber isBordered name={`${formPrefix}.capitalExpenditure`} />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                1 Year NOI
                              </TableCell>
                              <TableCell>
                                <FormFieldNumber isBordered name={`${formPrefix}.oneYearNoi`} />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Yield On Cost
                              </TableCell>
                              <TableCell>
                                <FormFieldNumber
                                  isFloat
                                  isPercent
                                  isBordered
                                  withNoLimit
                                  name={`${formPrefix}.yieldOnCost`}
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Debt Proceeds
                              </TableCell>
                              <TableCell>
                                <FormFieldNumber isBordered name={`${formPrefix}.debtProceeds`} />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Required Equity
                              </TableCell>
                              <TableCell>
                                <FormFieldNumber isBordered name={`${formPrefix}.requiredEquityAmount`} />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Level of Interest
                              </TableCell>
                              <TableCell>
                                <FormFieldSelect
                                  isBordered
                                  withBlankElement
                                  isDisabled={activityInterestLevelList && !activityInterestLevelList.length}
                                  name={`${formPrefix}.interestLevel`}
                                  optionsTotal={activityInterestLevelList && activityInterestLevelList.length}
                                >
                                  {menuItemActivityInterestLevelList}
                                </FormFieldSelect>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                LTC
                              </TableCell>
                              <TableCell>
                                <FormFieldNumber
                                  isFloat
                                  isPercent
                                  isBordered
                                  withNoLimit
                                  name={`${formPrefix}.ltc`}
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Equity Provider
                              </TableCell>
                              <TableCell>
                                <FormFieldSelect
                                  isBordered
                                  withSearch
                                  withBlankElement
                                  isDisabled={contactList && !contactList.length}
                                  name={`${formPrefix}.equityProviderId`}
                                  optionsTotal={contactList && contactList.length}
                                  onSearch={onContactSearch}
                                  onClose={onContactSearchClose}
                                >
                                  {menuItemContactList}
                                </FormFieldSelect>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Investor
                              </TableCell>
                              <TableCell>
                                <FormFieldSelect
                                  isBordered
                                  withSearch
                                  withBlankElement
                                  isDisabled={contactList && !contactList.length}
                                  name={`${formPrefix}.investorId`}
                                  optionsTotal={contactList && contactList.length}
                                  onSearch={onContactSearch}
                                  onClose={onContactSearchClose}
                                >
                                  {menuItemContactList}
                                </FormFieldSelect>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Lender Role
                              </TableCell>
                              <TableCell>
                                <FormFieldText isBordered name={`${formPrefix}.lenderRole`} />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Max Amount
                              </TableCell>
                              <TableCell>
                                <FormFieldText isBordered name={`${formPrefix}.maxAmount`} />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Loan Recourse
                              </TableCell>
                              <TableCell>
                                <FormFieldText isBordered name={`${formPrefix}.loanRecourse`} />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Additional Party
                              </TableCell>
                              <TableCell>
                                <FormFieldSelect
                                  isBordered
                                  withSearch
                                  withBlankElement
                                  isDisabled={contactList && !contactList.length}
                                  name={`${formPrefix}.additionalPartyId`}
                                  optionsTotal={contactList && contactList.length}
                                  onSearch={onContactSearch}
                                  onClose={onContactSearchClose}
                                >
                                  {menuItemContactList}
                                </FormFieldSelect>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Additional Party Details
                              </TableCell>
                              <TableCell>
                                <FormFieldText isBordered name={`${formPrefix}.additionalPartyDetails`} />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Tags
                              </TableCell>
                              <TableCell>
                                <FormFieldMultiSelect
                                  isBordered
                                  isAbleToAdd
                                  withSearch
                                  isDisabled={tagList && !tagList.length}
                                  keyField="name"
                                  name={`${formPrefix}.tags`}
                                  optionList={tagList}
                                  onSearch={onTagSearch}
                                  onClose={onTagSearchClose}
                                  onValuesChange={tagsChange}
                                >
                                  {menuItemTagList}
                                </FormFieldMultiSelect>
                              </TableCell>
                            </TableRow>
                            {
                              [
                                ...((activityFieldItems && Object.values(activityFieldItems)) || []),
                                ...(activityFieldNewItems || []),
                              ].map((activityFieldItem: Activity): ReactElement => {
                                const formKeyActivityField: string = FormService.getIndexedFormFieldKey(
                                  activityFieldItem.id,
                                );

                                return (
                                  <TableRow key={activityFieldItem.id}>
                                    <TableCell>
                                      <Fragment>
                                        <FormFieldText
                                          isHidden
                                          name={`${formPrefix}.options[${formKeyActivityField}].id`}
                                        />
                                        <FormFieldText
                                          isBordered
                                          name={`${formPrefix}.options[${formKeyActivityField}].name`}
                                          placeholder="Name"
                                        />
                                      </Fragment>
                                    </TableCell>
                                    <TableCell>
                                      <FlexContainer>
                                        <FormFieldText
                                          isBordered
                                          name={`${formPrefix}.options[${formKeyActivityField}].value`}
                                          placeholder="Value"
                                        />
                                        <Tooltip title="Delete">
                                          <ActionButton
                                            isInline
                                            withOffset
                                            withSmallPadding
                                            onClick={(): void => {
                                              deleteClick(activityFieldItem.id);
                                            }}
                                          >
                                            <RemoveIcon />
                                          </ActionButton>
                                        </Tooltip>
                                      </FlexContainer>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            }
                            <TableRowAddNewItem colSpan={COLUMNS_TOTAL + 1} onClick={addNewItem} />
                          </TableBody>
                        </Table>
                      </Form>
                    </Card>
                  </GridItem>
                )
              }
              {
                !isNewActivity && (
                  <CheckAccess
                    permissions={isDealActivity ? dealListViewPermissions : activityCommentListViewPermissions}
                  >
                    <GridItem isFullWidth>
                      <Card isContainer withNoPadding>
                        <CommentTable
                          currentUser={currentUser}
                          commentList={commentList}
                          queryCommentId={queryCommentId}
                          commentEditId={commentEditId}
                          commentActiveId={commentActiveId}
                          editPermissions={activityCommentEditPermissions}
                          deletePermissions={activityCommentDeletePermissions}
                          mentionPermission={CONTACT_VIEW}
                          hashtagPermission={SEARCH_RESULT_VIEW}
                          onSwitchCommentEditMode={switchCommentEditMode}
                          onDeleteCommentConfirm={onDeleteCommentConfirm}
                          onDeleteCommentCancel={onDeleteCommentCancel}
                          onCommentActiveIdChange={onCommentActiveIdChange}
                          onCommentEntityLinkClick={onCommentEntityLinkClick}
                        />
                      </Card>
                    </GridItem>
                  </CheckAccess>
                )
              }
            </Grid>
            {
              !isNewActivity && (
                <CheckAccess
                  permissions={isDealActivity ? dealListViewPermissions : activityCommentCreatePermissions}
                >
                  <CommentForm
                    isCommentSaving={isCommentSaving}
                    isCommentFormActive={isCommentFormActive}
                    withContactFields={!isCommentEditing}
                    attachmentFiles={commonAttachmentFiles}
                    commentText={commentText}
                    selectedContactCompanyContacts={selectedContactCompanyContacts}
                    selectedContactPersonContacts={selectedContactPersonContacts}
                    mentionList={mentionList}
                    hashtagList={hashtagList}
                    contactCompanyList={contactCompanyList}
                    contactPersonList={contactPersonList}
                    mentionRouteRoot={ROUTE_CONTACT}
                    hashtagRouteRoot={ROUTE_SEARCH}
                    commentAttachmentDropDeps={commentAttachmentDropDeps}
                    onCommentFieldChange={onCommentFieldChange}
                    onCommentFieldKeyDown={onCommentFieldKeyDown}
                    onSaveComment={saveComment}
                    onCommentAttachmentFilesChange={onCommentAttachmentFilesChange}
                    onCommentAttachmentFileDelete={onCommentAttachmentFileDelete}
                    onCommentAttachmentFileError={onCommentAttachmentFileError}
                    onContactCompanySearch={onContactCompanySearch}
                    onContactCompanySearchClose={onContactCompanySearchClose}
                    onContactPersonSearch={onContactPersonSearch}
                    onContactPersonSearchClose={onContactPersonSearchClose}
                    onCompanyContactsChange={onCompanyContactsChange}
                    onPersonContactsChange={onPersonContactsChange}
                  />
                </CheckAccess>
              )
            }
            <ModalButtonBlock isFixed={withFixedFooter}>
              {
                (commentText || isNewActivity || (commentAttachmentFiles && !!commentAttachmentFiles.length)) && (
                  <Button onClick={cancelChangesAndCloseModal}>
                    Cancel
                  </Button>
                )
              }
              <Button
                isDisabled={isSaving}
                isActive={!!commentText || isNewActivity || (commentAttachmentFiles
                  && !!commentAttachmentFiles.length)}
                onClick={applyChangesAndCloseModal}
              >
                {commentText || isNewActivity || (commentAttachmentFiles && commentAttachmentFiles.length)
                  ? 'Save' : 'Close'}
              </Button>
              {
                (isCommentSaving || (isSaving && isNewActivity)) && !isSmallScreen && (
                  <Loader isInline isModal isSmall isFloating={withFixedFooter} />
                )
              }
            </ModalButtonBlock>
            <LeaveConfirmModal
              isSubModal
              isOpen={isLeaveConfirmModalOpen}
              onConfirm={onLeaveConfirm}
              onCloseModal={closeLeaveConfirmModal}
            />
          </ModalLayout>
        )
      }
      <StatusBar isOpen={isOpenMessage} isError={isMessageError} message={statusMessage} onClose={closeMessage} />
    </Fragment>
  );
});

export default ActivityModal;
